import React, { useId } from "react";
import { Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";

const PILL_BASE =
  "inline-flex cursor-pointer select-none items-center rounded-full border px-3 py-1.5 text-xs font-medium transition-colors " +
  "peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2";
const PILL_ON = "border-indigo-300 bg-indigo-600 text-white";
const PILL_OFF = "border-input bg-background text-muted-foreground hover:bg-muted hover:text-foreground";

function FillsTemplates() {
  return (
    <Badge variant="outline" className="gap-1 border-violet-200 bg-violet-50 text-[10px] font-medium text-violet-700">
      <Sparkles className="h-3 w-3" /> Fills templates
    </Badge>
  );
}

function Required() {
  return (
    <>
      <span className="ml-0.5 text-destructive" aria-hidden="true">
        *
      </span>
      <span className="sr-only"> (required)</span>
    </>
  );
}

/**
 * IntakeField — renders one field of the Project / Brand Intake.
 *
 * Handles every type in data/agreements/intakeSchema.js: text, textarea, select,
 * multiselect (toggleable chips) and radio (a row of pills). An unknown type falls back
 * to a text input rather than rendering nothing.
 *
 * props
 *   field     { id, label, type, options, help, placeholder, required, brandKey }
 *   value     string for text/textarea/select/radio, string[] for multiselect
 *   onChange  (nextValue) => void
 */
export default function IntakeField({ field, value, onChange, className }) {
  const autoId = useId();
  if (!field) return null;

  const id = field.id || `intake-${autoId}`;
  const helpId = field.help ? `${id}-help` : undefined;
  const labelId = `${id}-label`;
  const type = field.type || "text";
  const options = (Array.isArray(field.options) ? field.options : []).filter(
    (o) => o && o.value !== undefined && o.value !== null && String(o.value) !== "",
  );

  const help = field.help ? (
    <p id={helpId} className="text-xs text-muted-foreground">
      {field.help}
    </p>
  ) : null;

  const emptyOptions = (
    <p className="rounded-md border border-dashed px-3 py-2 text-xs text-muted-foreground">No options configured.</p>
  );

  // ── Grouped controls: a fieldset carries the label for the whole set ───────
  if (type === "multiselect" || type === "radio") {
    const isMulti = type === "multiselect";
    const selected = isMulti ? (Array.isArray(value) ? value : []) : [];
    const toggle = (v) =>
      onChange?.(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);

    return (
      <fieldset className={cn("min-w-0 border-0 p-0", className)}>
        <legend className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-1 p-0">
          <span id={labelId} className="text-sm font-medium leading-none">
            {field.label}
            {field.required && <Required />}
          </span>
          {field.brandKey && <FillsTemplates />}
        </legend>
        {help && <div className="mb-2">{help}</div>}
        {options.length === 0 ? (
          emptyOptions
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {options.map((opt) => {
              const on = isMulti ? selected.includes(opt.value) : value === opt.value;
              return (
                <label key={String(opt.value)} className="inline-flex">
                  <input
                    type={isMulti ? "checkbox" : "radio"}
                    name={isMulti ? `${id}-${opt.value}` : id}
                    value={String(opt.value)}
                    checked={on}
                    aria-describedby={helpId}
                    onChange={() => (isMulti ? toggle(opt.value) : onChange?.(opt.value))}
                    className="peer sr-only"
                  />
                  <span className={cn(PILL_BASE, on ? PILL_ON : PILL_OFF)}>{opt.label ?? String(opt.value)}</span>
                </label>
              );
            })}
          </div>
        )}
      </fieldset>
    );
  }

  // ── Single controls ───────────────────────────────────────────────────────
  let control;
  if (type === "textarea") {
    control = (
      <Textarea
        id={id}
        rows={3}
        value={typeof value === "string" ? value : ""}
        placeholder={field.placeholder}
        aria-describedby={helpId}
        aria-required={field.required || undefined}
        onChange={(e) => onChange?.(e.target.value)}
      />
    );
  } else if (type === "select") {
    control =
      options.length === 0 ? (
        emptyOptions
      ) : (
        <Select value={typeof value === "string" ? value : ""} onValueChange={(v) => onChange?.(v)}>
          <SelectTrigger id={id} aria-describedby={helpId} aria-required={field.required || undefined}>
            <SelectValue placeholder={field.placeholder || "Select…"} />
          </SelectTrigger>
          <SelectContent>
            {options.map((opt) => (
              <SelectItem key={String(opt.value)} value={String(opt.value)}>
                {opt.label ?? String(opt.value)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
  } else {
    control = (
      <Input
        id={id}
        type="text"
        value={typeof value === "string" ? value : ""}
        placeholder={field.placeholder}
        aria-describedby={helpId}
        aria-required={field.required || undefined}
        onChange={(e) => onChange?.(e.target.value)}
      />
    );
  }

  return (
    <div className={cn("min-w-0 space-y-1.5", className)}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Label htmlFor={id} className="text-sm">
          {field.label}
          {field.required && <Required />}
        </Label>
        {field.brandKey && <FillsTemplates />}
      </div>
      {control}
      {help}
    </div>
  );
}
